import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { luxuryEase, prefersReducedMotion } from '../lib/motion';
import { companyInfo } from '../data/siteData';

const LOGO_MS = 1000;

function pickGrid() {
  const w = window.visualViewport?.width ?? window.innerWidth;
  const h = window.visualViewport?.height ?? window.innerHeight;
  const portrait = h >= w;

  let cols;
  let rows;
  if (w < 640) {
    cols = portrait ? 4 : 7;
    rows = portrait ? 8 : 4;
  } else if (w < 1024) {
    cols = portrait ? 6 : 8;
    rows = portrait ? 8 : 5;
  } else if (w >= 1536) {
    cols = 12;
    rows = 7;
  } else {
    cols = 10;
    rows = 6;
  }

  const maxDiag = cols - 1 + rows - 1;
  return {
    cols,
    rows,
    stagger: 0.7 / Math.max(maxDiag, 1),
    duration: w < 640 ? 0.5 : 0.55,
  };
}

function useGrid() {
  const [grid, setGrid] = useState({ cols: 10, rows: 6, stagger: 0.055, duration: 0.55 });

  useEffect(() => {
    setGrid(pickGrid());
  }, []);

  return grid;
}

export default function OpeningReel({ onReveal, onComplete, caption = 'Nosta Yapı' }) {
  const [phase, setPhase] = useState('logo');
  const { cols, rows, stagger, duration } = useGrid();
  const revealRef = useRef(onReveal);
  const doneRef = useRef(onComplete);
  revealRef.current = onReveal;
  doneRef.current = onComplete;
  const finished = useRef(false);
  const revealed = useRef(false);

  const tiles = useMemo(() => {
    const list = [];
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        const diag = cols - 1 - c + r;
        list.push({
          key: `${c}-${r}`,
          delay: diag * stagger,
        });
      }
    }
    return list;
  }, [cols, rows, stagger]);

  const lastDelay = tiles.reduce((max, t) => Math.max(max, t.delay), 0);

  const revealPage = () => {
    if (revealed.current) return;
    revealed.current = true;
    revealRef.current?.();
  };

  const finish = () => {
    if (finished.current) return;
    finished.current = true;
    revealPage();
    doneRef.current();
  };

  useEffect(() => {
    if (prefersReducedMotion()) {
      finish();
      return undefined;
    }
    revealPage();
    const startReveal = setTimeout(() => setPhase('reveal'), LOGO_MS);
    return () => clearTimeout(startReveal);
  }, []);

  useEffect(() => {
    if (phase !== 'reveal') return undefined;
    const doneTimer = setTimeout(finish, (lastDelay + duration + 0.12) * 1000);
    return () => clearTimeout(doneTimer);
  }, [phase, lastDelay, duration]);

  return (
    <motion.div
      className={`fixed inset-0 z-[90] cursor-pointer ${
        phase === 'reveal' ? 'bg-transparent' : 'bg-nosta-ink'
      }`}
      data-intro-phase={phase}
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: luxuryEase }}
      onClick={finish}
      role="presentation"
    >
      <AnimatePresence>
        {phase === 'logo' && (
          <motion.div
            key="logo"
            className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 sm:gap-6 px-6 pointer-events-none pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.55, ease: luxuryEase }}
          >
            <img
              src={companyInfo.logoUrl}
              alt={companyInfo.name}
              className="h-16 sm:h-20 md:h-24 lg:h-[6.75rem] w-auto object-contain brightness-0 invert"
            />
            <p className="eyebrow text-nosta-goldSoft text-[10px] sm:text-[11px]">{caption}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {phase === 'reveal' && (
        <div
          className="absolute inset-0 grid gap-px"
          style={{
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
          }}
        >
          {tiles.map((tile) => (
            <motion.div
              key={tile.key}
              className="min-h-0 min-w-0 bg-nosta-ink origin-center"
              initial={{ scale: 1, opacity: 1 }}
              animate={{ scale: 0, opacity: 0 }}
              transition={{ duration, delay: tile.delay, ease: luxuryEase }}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}
